import {observer} from "mobx-react";
import {FormModel} from "../../../models/forms/form.model";
import {
    BookingSearchStrategyType, IBookingSearchStrategyViewModel
} from "../../../services/agency-booking-search/strategies/booking-search.strategy.interface";
import {FormFields} from "../../../models/forms/form-field.interface";
import React, {useEffect, useRef} from "react";
import {useServices} from "../../../hooks/use-services.hook";
import {StandardDropDownComponent} from "../../../components/forms/standard-drop-down/standard-drop-down.component";
import {
    StandardDropDownListItemModel
} from "../../../components/forms/standard-drop-down/list-view/models/item/standard-drop-down-list-item.model";
import styled from "styled-components";
import {IonIcon} from "@ionic/react";
import {closeCircleOutline} from "ionicons/icons";
import {IServiceFactory} from "../../../services/service-factory.interface";
import {IReactionDisposer, reaction} from "mobx";

const StrategyDropDownItemBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${props => props.theme.spacing.flexGap8};
    font-size: ${props => props.theme.fontSize.body1};
    font-weight: ${props => props.theme.fontWeight.medium};
`

const StrategyDropDownItemComponent: React.FC<{strategy: IBookingSearchStrategyViewModel}> = (props) => {
    return (
        <StrategyDropDownItemBox>
            {props.strategy.renderIcon()}
            <span>
                {props.strategy.name}
            </span>
        </StrategyDropDownItemBox>
    )
}

const SelectedStrategyIconBox = styled.span`
    padding-top: 4px;
`




class SearchStrategyDropDownItemModel extends StandardDropDownListItemModel<BookingSearchStrategyType> {
    constructor(private readonly strategy: IBookingSearchStrategyViewModel) {
        super(strategy.type, strategy.name);
    }

    protected _renderItemContent(): string | React.ReactElement {
        return (<StrategyDropDownItemComponent strategy={this.strategy}/>)
    }
}

const CSS_CLASS_ICON_AND_INPUT_CONTAINER = "search-strategies-dropdown-input-and-icon";

const DropDownBox = styled(StandardDropDownComponent)`
    & .${CSS_CLASS_ICON_AND_INPUT_CONTAINER} {
        font-size: ${props => props.theme.fontSize.body1};
        font-weight: ${props => props.theme.fontWeight.medium};
    }
`

const DropDownLabelContainerBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-bottom: 2px;
    padding-right: 2px;
    
`

const ClearFiltersContainerBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;
`

const ClearFiltersButtonBox = styled.div`
    cursor: pointer;
    font-size: ${props => props.theme.fontSize.body2};
    ${props => props.theme.screenMediaQuery.hover} {
        &:hover {
            text-decoration: underline;
        }    
    }
    
`

const ClearFiltersButtonComponent: React.FC = observer(() => {
    const services = useServices();
    const onClearFilters = () => {
        services.agencyBookingSearch.currentStrategy.clearAllFilters();
    }

    return (
        <ClearFiltersContainerBox>
            <IonIcon icon={closeCircleOutline}/>
            <ClearFiltersButtonBox onClick={onClearFilters}>
                {services.language.translate('Clear all filters')}
            </ClearFiltersButtonBox>
        </ClearFiltersContainerBox>
    )
})

class StrategySelectionForm extends FormModel<{ strategyType: BookingSearchStrategyType }> {
    constructor(services: IServiceFactory) {
        super(services);
        this._reactionDispose = reaction(() =>   this.services.agencyBookingSearch.currentStrategy,
            (strategy) => {
                if(this.fields.strategyType.value !== strategy.type) {
                    this.fields.strategyType.setValue(strategy.type);
                }
        });
    }

    dispose() {
        this._reactionDispose();
    }

    private readonly _reactionDispose: IReactionDisposer;
    protected _createFields(): FormFields<{ strategyType: BookingSearchStrategyType; }> {
        return {
            strategyType: this._createField({
                fieldName: () => this.services.language.translate('Search'),
                isRequired: true,
                initialValue: () => this.services.agencyBookingSearch.currentStrategy.type
            })
        }
    }

    protected _onFieldsCreated(fields: FormFields<{ strategyType: BookingSearchStrategyType }>) {
        super._onFieldsCreated(fields);
        fields.strategyType.onChange((newValue) => {
            if(newValue) {
                this.services.agencyBookingSearch.setCurrentStrategy(newValue);
            }
        });
    }
}
export const BookingSearchStrategiesDropDownComponent: React.FC = observer(() => {
    const services = useServices();
    const formRef = useRef(new StrategySelectionForm(services));

    const strategies = services.agencyBookingSearch.searchStrategies;
    const dropDownItems = strategies.map(s => new SearchStrategyDropDownItemModel(s));

    const renderIcon = () => {
        const icon = services.agencyBookingSearch.currentStrategy.renderIcon();
        if(!icon) {
            return null;
        }

        return (
            <SelectedStrategyIconBox>
                {icon}
            </SelectedStrategyIconBox>

        );
    }

    useEffect(() => {
        let form = formRef.current;

        if(!form) {
           formRef.current = form = new StrategySelectionForm(services);
        }

        return () => {
            form?.dispose();
        }
    }, [services]);


    const renderLabel = () => {
        return (
            <DropDownLabelContainerBox>
                <span>
                    {formRef.current.fields.strategyType.fieldName}
                </span>
                <ClearFiltersButtonComponent/>
            </DropDownLabelContainerBox>
        )
    }

    return (
        <DropDownBox field={formRef.current.fields.strategyType}
                     items={dropDownItems}
                     renderLabel={renderLabel}
                     renderIcon={renderIcon}
                     cssClassesForOutsideBordersStyle={{
                       inputAndIconContainer: CSS_CLASS_ICON_AND_INPUT_CONTAINER,
                     }}/>
    );
})